import { aiWidgetAliases } from 'components/AiAnalytics/shared/aiWidgetActions';
import { hasRenderableWidgetData } from 'components/AiAnalytics/shared/aiWidgetGuards';
import {
  ConversationBalanceApiResponse,
  ConversationBalanceMetricName,
} from 'components/AiAnalytics/shared/aiWidgetResponse.types';
import { ConversationBalanceViewMetric } from './ConversationBalance.types';

const metricOrder: ConversationBalanceMetricName[] = [
  'message_count',
  'handle_time',
  'response_time',
  'message_length',
];

const roles = [
  { id: 'agent', label: 'Agent', color: '#174866' },
  { id: 'customer', label: 'Customer', color: '#8a3ffc' },
  { id: 'chatbot', label: 'Chatbot', color: '#168f86' },
] as const;

export const mapConversationBalanceResponse = (
  response: unknown
): ConversationBalanceViewMetric[] | null => {
  if (!hasRenderableWidgetData(aiWidgetAliases.CONVERSATION_BALANCE, response)) return null;

  const { data } = response as ConversationBalanceApiResponse;
  const metricsByName = new Map(data.metrics.map((metric) => [metric.name, metric] as const));

  if (
    metricsByName.size !== metricOrder.length ||
    metricOrder.some((name) => !metricsByName.has(name))
  ) {
    return null;
  }

  if (
    metricOrder.some((key) => {
      const metric = metricsByName.get(key);
      if (!metric) return true;

      const values = new Map(metric.series.map((point) => [point.role, point.value] as const));
      return values.size !== roles.length || roles.some(({ id }) => !values.has(id));
    })
  ) {
    return null;
  }

  return metricOrder.map((key) => {
    const metric = metricsByName.get(key);
    if (!metric) throw new Error(`Missing Conversation Balance metric: ${key}`);

    const values = new Map(metric.series.map((point) => [point.role, point] as const));

    const segments = roles.map(({ id, label, color }) => {
      const point = values.get(id);
      if (!point) throw new Error(`Missing Conversation Balance role: ${id}`);

      return {
        id,
        label,
        color,
        value: point.value,
        rawValue: point.rawValue,
      };
    });

    return {
      key,
      label: metric.label,
      unit: metric.unit,
      segments,
      hasActivity: segments.some((segment) => segment.value > 0),
    };
  });
};
